import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileText, Plus, CheckCircle2, AlertTriangle, Clock, Link, Bell, MoreVertical, Eye } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../lib/api';

const statusConfig = {
  pending: { color: 'bg-amber-100 text-amber-800', label: 'Pending', icon: Clock },
  submitted: { color: 'bg-blue-100 text-blue-800', label: 'Submitted', icon: FileText },
  approved: { color: 'bg-green-100 text-green-800', label: 'Approved', icon: CheckCircle2 },
  invoiced: { color: 'bg-purple-100 text-purple-800', label: 'Invoiced', icon: FileText },
};

export const TimesheetsPage = () => {
  const [timesheets, setTimesheets] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  const [formData, setFormData] = useState({
    contract_id: '',
    company_id: '',
    month: new Date().toISOString().slice(0, 7),
    client_name: '',
    client_email: '',
    project_name: '',
    hours_worked: '',
    rate_per_hour: '',
    amount: 0,
    notes: ''
  });

  const [linkContractId, setLinkContractId] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [timesheetsRes, contractsRes, companiesRes] = await Promise.all([
        api.get('/timesheets'),
        api.get('/contracts'),
        api.get('/companies')
      ]);
      setTimesheets(timesheetsRes.data);
      setContracts(contractsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleContractChange = (contractId) => {
    if (contractId === 'none') {
      setFormData({
        ...formData,
        contract_id: '',
        company_id: companies[0]?.id || ''
      });
    } else {
      const contract = contracts.find(c => c.id === contractId);
      setFormData({
        ...formData,
        contract_id: contractId,
        company_id: contract?.company_id || '',
        client_name: contract?.client_name || ''
      });
    }
  };

  const calculateAmount = (hours, rate) => {
    const amount = parseFloat(hours || 0) * parseFloat(rate || 0);
    setFormData(prev => ({ ...prev, amount }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_id) {
      toast.error('Please select a company');
      return;
    }
    
    if (!formData.contract_id && !formData.client_name) {
      toast.error('Please provide client name when no contract is selected');
      return;
    }

    try {
      await api.post('/timesheets', formData);
      toast.success('Timesheet created successfully!');
      setDialogOpen(false);
      loadData();
      setFormData({
        contract_id: '',
        company_id: '',
        month: new Date().toISOString().slice(0, 7),
        client_name: '',
        client_email: '',
        project_name: '',
        hours_worked: '',
        rate_per_hour: '',
        amount: 0,
        notes: ''
      });
    } catch (error) {
      toast.error('Failed to create timesheet');
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.patch(`/timesheets/${id}/approve`);
      toast.success('Timesheet approved!');
      loadData();
    } catch (error) {
      toast.error('Failed to approve timesheet');
    }
  };

  const handleSubmitForApproval = async (id) => {
    try {
      await api.patch(`/timesheets/${id}/submit`);
      toast.success('Timesheet submitted for approval!');
      loadData();
    } catch (error) {
      toast.error('Failed to submit timesheet');
    }
  };

  const handleView = (timesheet) => {
    setSelectedTimesheet(timesheet);
    setViewDialogOpen(true);
  };

  const openLinkDialog = (timesheet) => {
    setSelectedTimesheet(timesheet);
    setLinkContractId('');
    setLinkDialogOpen(true);
  };

  const handleLinkContract = async () => {
    if (!linkContractId) {
      toast.error('Please select a contract');
      return;
    }
    try {
      await api.patch(`/timesheets/${selectedTimesheet.id}/link-contract?contract_id=${linkContractId}`);
      toast.success('Timesheet linked to contract!');
      setLinkDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to link contract');
    }
  };

  const handleTriggerFollowUp = async (timesheet) => {
    try {
      await api.post(`/timesheets/${timesheet.id}/follow-up`);
      toast.success('Follow-up triggered!');
      loadData();
    } catch (error) {
      toast.error('Failed to trigger follow-up');
    }
  };

  const getClientName = (timesheet) => {
    if (timesheet.client_name) return timesheet.client_name;
    const contract = contracts.find(c => c.id === timesheet.contract_id);
    return contract?.client_name || 'N/A';
  };

  const filteredTimesheets = timesheets.filter(ts => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending_po') return ts.pending_po;
    if (activeTab === 'pending') return ts.status === 'pending' || ts.status === 'submitted';
    if (activeTab === 'approved') return ts.status === 'approved' || ts.status === 'invoiced';
    return true;
  });

  const pendingPOCount = timesheets.filter(ts => ts.pending_po).length;

  return (
    <div className="space-y-6" data-testid="timesheets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="timesheets-title">Timesheets</h1>
          <p className="text-muted-foreground mt-1">{timesheets.length} total timesheets</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-timesheet-button">
              <Plus className="h-4 w-4 mr-2" />
              New Timesheet
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Timesheet</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Contract (Optional)</Label>
                <Select value={formData.contract_id || 'none'} onValueChange={handleContractChange}>
                  <SelectTrigger data-testid="timesheet-contract-select">
                    <SelectValue placeholder="Select contract or leave empty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Contract (Pending PO)</SelectItem>
                    {contracts.filter(c => c.status === 'active' || c.status === 'po_received').map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.contract_number} - {c.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!formData.contract_id && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Timesheet will be marked as &quot;Pending PO&quot; - auto follow-ups enabled
                  </p>
                )}
              </div>

              {!formData.contract_id && (
                <>
                  <div>
                    <Label>Company *</Label>
                    <Select value={formData.company_id} onValueChange={(v) => setFormData({ ...formData, company_id: v })} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select company" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Client Name *</Label>
                      <Input
                        value={formData.client_name}
                        onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                        required={!formData.contract_id}
                        placeholder="Client company name"
                      />
                    </div>
                    <div>
                      <Label>Client Email</Label>
                      <Input
                        type="email"
                        value={formData.client_email}
                        onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                        placeholder="For follow-ups"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Project Name</Label>
                    <Input
                      value={formData.project_name}
                      onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                      placeholder="Project or site name"
                    />
                  </div>
                </>
              )}

              <div>
                <Label>Month *</Label>
                <Input
                  type="month"
                  value={formData.month}
                  onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                  required
                  data-testid="month-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Hours Worked *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={formData.hours_worked}
                    onChange={(e) => {
                      setFormData({ ...formData, hours_worked: e.target.value });
                      calculateAmount(e.target.value, formData.rate_per_hour);
                    }}
                    required
                    data-testid="hours-input"
                  />
                </div>
                <div>
                  <Label>Rate per Hour (SAR) *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.rate_per_hour}
                    onChange={(e) => {
                      setFormData({ ...formData, rate_per_hour: e.target.value });
                      calculateAmount(formData.hours_worked, e.target.value);
                    }}
                    required
                    data-testid="rate-input"
                  />
                </div>
              </div>
              <div>
                <Label>Total Amount</Label>
                <Input
                  value={`SAR ${formData.amount.toLocaleString()}`}
                  disabled
                  className="font-bold"
                  data-testid="amount-display"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-timesheet-button">
                  Create Timesheet
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({timesheets.length})</TabsTrigger>
          <TabsTrigger value="pending_po" className="relative">
            Pending PO
            {pendingPOCount > 0 && (
              <Badge className="ml-2 bg-amber-500 text-white">{pendingPOCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="pending">Pending Approval</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading timesheets...</div>
        </div>
      ) : filteredTimesheets.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No timesheets found</h3>
          <p className="text-muted-foreground mb-4">Start tracking work hours</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTimesheets.map((timesheet) => {
            const StatusIcon = statusConfig[timesheet.status]?.icon || Clock;
            return (
              <Card key={timesheet.id} className="p-6 hover:shadow-lg transition-shadow duration-200" data-testid={`timesheet-card-${timesheet.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-bold" data-testid={`timesheet-month-${timesheet.id}`}>{timesheet.month}</h3>
                    <p className="text-sm text-muted-foreground">{getClientName(timesheet)}</p>
                    {timesheet.project_name && (
                      <p className="text-xs text-muted-foreground">{timesheet.project_name}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    <Badge className={statusConfig[timesheet.status]?.color} data-testid={`timesheet-status-${timesheet.id}`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusConfig[timesheet.status]?.label}
                    </Badge>
                    {timesheet.pending_po && (
                      <Badge className="bg-amber-100 text-amber-800">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Pending PO
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Hours:</span>
                    <span className="font-semibold">{timesheet.hours_worked || 0} hrs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rate:</span>
                    <span>{(timesheet.rate_per_hour || 0).toLocaleString()} SAR/hr</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-muted-foreground font-semibold">Amount:</span>
                    <span className="font-bold text-lg">{timesheet.amount.toLocaleString()} SAR</span>
                  </div>
                  {timesheet.pending_po && timesheet.po_follow_up_count > 0 && (
                    <div className="text-xs text-amber-600 flex items-center gap-1">
                      <Bell className="h-3 w-3" />
                      {timesheet.po_follow_up_count} follow-up(s) sent
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2">
                  {timesheet.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleSubmitForApproval(timesheet.id)}
                    >
                      Submit for Approval
                    </Button>
                  )}
                  {timesheet.status === 'submitted' && (
                    <Button
                      size="sm"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(timesheet.id)}
                      data-testid={`approve-timesheet-${timesheet.id}`}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                  )}
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleView(timesheet)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      {timesheet.pending_po && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openLinkDialog(timesheet)}>
                            <Link className="h-4 w-4 mr-2" />
                            Link to Contract
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleTriggerFollowUp(timesheet)}>
                            <Bell className="h-4 w-4 mr-2" />
                            Send Follow-up
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Timesheet Details</DialogTitle>
          </DialogHeader>
          
          {selectedTimesheet && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold">{selectedTimesheet.month}</h3>
                  <p className="text-muted-foreground">{getClientName(selectedTimesheet)}</p>
                </div>
                <div className="flex flex-col gap-1 items-end">
                  <Badge className={statusConfig[selectedTimesheet.status]?.color}>
                    {statusConfig[selectedTimesheet.status]?.label}
                  </Badge>
                  {selectedTimesheet.pending_po && (
                    <Badge className="bg-amber-100 text-amber-800">Pending PO</Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Hours Worked</p>
                  <p className="font-medium">{selectedTimesheet.hours_worked} hrs</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Rate</p>
                  <p className="font-medium">SAR {selectedTimesheet.rate_per_hour}/hr</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total Amount</p>
                  <p className="font-bold text-lg">SAR {selectedTimesheet.amount?.toLocaleString()}</p>
                </div>
                {selectedTimesheet.project_name && (
                  <div>
                    <p className="text-muted-foreground">Project</p>
                    <p className="font-medium">{selectedTimesheet.project_name}</p>
                  </div>
                )}
              </div>

              {selectedTimesheet.pending_po && (
                <div className="bg-amber-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-amber-800 mb-1">Pending PO</h4>
                  <p className="text-sm text-amber-700">
                    Follow-ups sent: {selectedTimesheet.po_follow_up_count || 0}
                  </p>
                  {selectedTimesheet.last_follow_up_date && (
                    <p className="text-xs text-amber-600">
                      Last follow-up: {new Date(selectedTimesheet.last_follow_up_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}

              {selectedTimesheet.notes && (
                <div>
                  <p className="text-muted-foreground text-sm">Notes</p>
                  <p className="mt-1">{selectedTimesheet.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Link Contract Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Link to Contract</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Link this timesheet to a contract once the PO has been received.
            </p>
            <div>
              <Label>Select Contract *</Label>
              <Select value={linkContractId} onValueChange={setLinkContractId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select contract" />
                </SelectTrigger>
                <SelectContent>
                  {contracts.filter(c => c.status === 'po_received' || c.status === 'active').map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.contract_number} - {c.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleLinkContract}>Link Contract</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
